/**
 * A coach drawn as it is: `seat_rows` lists each row's seat labels in physical left-to-right order
 * (the backend fills it for every operator), `corridor_after` says where the aisle is.
 * Rows can be short (a wheelchair bay): a missing seat is an empty cell, later rows do not shift.
 */
import type { Coach } from './session';

export type SeatPosition = 'window' | 'aisle' | 'middle';

export interface SeatCell {
  label: string;
  occupied: boolean;
  position: SeatPosition;
}

export interface CoachGrid {
  number: number;
  className: string;
  /** Each row of the coach, its seats in order; `null` is a gap. */
  rows: (SeatCell | null)[][];
  /** Index after which the aisle runs (0 = unknown). */
  aisleAfter: number;
  width: number;
}

export function seatPosition(index: number, rowLength: number, corridorAfter: number): SeatPosition {
  if (rowLength <= 1 || index === 0 || index === rowLength - 1) return 'window';
  if (corridorAfter > 0 && (index === corridorAfter - 1 || index === corridorAfter)) return 'aisle';
  return 'middle';
}

export function coachGrid(coach: Coach): CoachGrid {
  const occupied = new Set(coach.occupied);
  const width = Math.max(0, ...coach.seat_rows.map((r) => r.length));
  const rows = coach.seat_rows.map((row) => {
    const cells: (SeatCell | null)[] = row.map((label, i) => ({
      label, occupied: occupied.has(label), position: seatPosition(i, row.length, coach.corridor_after),
    }));
    while (cells.length < width) cells.push(null);
    return cells;
  });
  return { number: coach.coach_number, className: coach.display_class || coach.class_name, rows, aisleAfter: coach.corridor_after, width };
}

/** Toggle a seat in the picked list: up to `needed`, dropping the oldest pick when full. */
export function togglePick(picked: readonly string[], seat: string, needed: number): string[] {
  if (picked.includes(seat)) return picked.filter((s) => s !== seat);
  const next = [...picked, seat];
  return next.length > needed ? next.slice(next.length - needed) : next;
}
